import { useNavigate, useOutletContext } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import TextAreaChat from '@/components/TextAreaChat';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { Content, Model } from '@shared/types';
import { MessageItem, MeshUploadState } from '@/types/misc';
import { cn } from '@/lib/utils';
import { SelectedItemsContext } from '@/contexts/SelectedItemsContext';
import { useSendContentMutation } from '@/services/messageService';
import { generateConversationTitle } from '@/services/conversationService';

export function PromptView() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isLoading } = useAuth();
  const { isSidebarOpen, isMobile } = useOutletContext<{ isSidebarOpen: boolean; isMobile: boolean }>();
  const queryClient = useQueryClient();

  const [model, setModel] = useState<Model>('google/gemini-3-pro-preview');
  const [isLoaded, setIsLoaded] = useState(false);
  const [images, setImages] = useState<MessageItem[]>([]);
  const [meshUpload, setMeshUpload] = useState<MeshUploadState | null>(null);

  const newConversationId = useMemo(() => {
    return crypto.randomUUID();
  }, []);

  const { mutate: sendMessage } = useSendContentMutation({
    conversation: {
      id: newConversationId,
      user_id: user?.id ?? '',
      current_message_leaf_id: null,
    },
  });

  // Trigger fade in on mount
  useEffect(() => {
    // Use requestAnimationFrame to ensure the initial render is complete
    const frame = requestAnimationFrame(() => {
      setIsLoaded(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // Helper function to get time-based greeting (memoized for performance)
  const getTimeBasedGreeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return 'Good morning';
    } else if (hour < 18) {
      return 'Good afternoon';
    } else {
      return 'Good evening';
    }
  }, []); // Empty dependency array means it only calculates once per page load

  // Check if auth modal would be visible (user is not logged in)
  const isAuthModalVisible = !user;

  const { mutate: handleGenerate } = useMutation({
    mutationFn: async (content: Content) => {
      // Create conversation with the pre-generated ID so sendMessage can find it
      const conversation = await api.createConversation('New Conversation', newConversationId);

      sendMessage(content);

      // Generate title in the background (don't await)
      generateConversationTitle(conversation.id, content)
        .then(async (title) => {
          // Update conversation with generated title
          await api.updateConversation(conversation.id, { title });

          // Invalidate queries to refresh UI
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
          queryClient.invalidateQueries({
            queryKey: ['conversation', conversation.id],
          });
        })
        .catch((error) => {
          console.error('Failed to generate title:', error);
          // Don't show error to user, just log it
        });

      return {
        conversationId: conversation.id,
        content: content,
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      navigate(`/editor/${data.conversationId}`);
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to process prompt',
        variant: 'destructive',
      });
    },
  });

  return (
    <div
      className={cn(
        'relative h-full min-h-full transition-all duration-300 ease-in-out',
        !isMobile && isSidebarOpen && 'pb-6 pr-6 pt-6',
      )}
    >
      <div
        className={cn(
          'h-full min-h-full bg-adam-bg-secondary-dark',
          !isMobile && isSidebarOpen && 'rounded-xl shadow-[0_0_15px_rgba(0,0,0,0.1)]',
        )}
      >
        <main className={cn(
          'w-full px-4',
          isMobile ? 'pt-6' : 'pt-14 sm:pt-12 md:px-8'
        )}>
          <div className={cn(
            'mx-auto flex max-w-3xl flex-col items-center justify-center',
            isMobile ? 'mt-8' : 'mt-20'
          )}>
            <h1
              className={cn(
                'mb-6 text-center font-medium text-adam-text-primary md:mb-8',
                isMobile ? 'text-xl' : 'text-2xl md:text-3xl lg:text-4xl',
                isLoaded ? 'opacity-100' : 'opacity-0',
              )}
            >
              {getTimeBasedGreeting}!
            </h1>
          </div>
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'w-full max-w-3xl space-y-4 pb-12 transition-all duration-300',
                isAuthModalVisible && !isMobile && 'xl:max-w-2xl',
              )}
            >
              <SelectedItemsContext.Provider
                value={{ images, setImages, meshUpload, setMeshUpload }}
              >
                <TextAreaChat
                  onSubmit={handleGenerate}
                  conversation={{
                    id: newConversationId,
                    user_id: user?.id ?? '',
                  }}
                  placeholder="Start building with Adam..."
                  model={model}
                  setModel={setModel}
                  showPromptGenerator={true}
                />
              </SelectedItemsContext.Provider>
              <div className="relative">
                {isLoading && (
                  <div className="absolute left-0 right-0 top-0">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-adam-blue border-t-transparent" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
